import type {
  AbilityGrade,
  AssistantCoach,
  CoachDevelopmentAllocation,
  GameState,
  StaffMarketCandidate,
  StaffMarketEffects,
  StaffMarketState,
  StaffMember,
  StaffPhilosophy,
  StaffRatings,
  StaffSpecialty,
} from './types'

const SPECIALTIES: StaffSpecialty[] = ['attack', 'defence', 'development', 'conditioning']
const PHILOSOPHIES: StaffPhilosophy[] = ['front-foot', 'structured', 'teacher-led', 'availability-first', 'adaptive']
const FIRST_NAMES = ['Anika', 'Bryn', 'Celia', 'Darius', 'Elena', 'Flynn', 'Gia', 'Hamish', 'Indira', 'Jonah', 'Kira', 'Luca']
const LAST_NAMES = ['Ash', 'Baird', 'Cole', 'Dunn', 'Eames', 'Foley', 'Gale', 'Holt', 'Ives', 'Jett', 'Kemp', 'Lane']

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const money = (value: number) => Math.round(value * 100) / 100
const hash = (value: string) => {
  let result = 2166136261
  for (let index = 0; index < value.length; index += 1) result = Math.imul(result ^ value.charCodeAt(index), 16777619)
  return result >>> 0
}
const seeded = (seed: number, key: string, min: number, max: number) => min + (hash(`${seed}:${key}`) % (max - min + 1))
const roundMultiplier = (value: number) => Math.round(value * 20) / 20
const validNumber = (value: unknown, fallback: number, min = 0, max = 100) =>
  Number.isFinite(value) ? clamp(Number(value), min, max) : fallback

const specialtyFor = (member: StaffMember): StaffSpecialty => {
  const role = member.role.toLowerCase()
  if (role.includes('forward')) return 'attack'
  if (role.includes('back')) return 'defence'
  if (role.includes('fitness') || role.includes('performance')) return 'conditioning'
  return 'development'
}

const initialDetails: Record<string, Pick<AssistantCoach, 'specialty' | 'philosophy' | 'philosophyFit' | 'ratings' | 'salary' | 'contractYears' | 'reputation' | 'loyalty' | 'ambition' | 'poachingRisk'>> = {
  'grant-havel': { specialty: 'defence', philosophy: 'structured', philosophyFit: 82, ratings: { coaching: 72, tactics: 74, development: 64, communication: 68, conditioning: 52 }, salary: 0.54, contractYears: 3, reputation: 68, loyalty: 78, ambition: 61, poachingRisk: 0 },
  'mara-keane': { specialty: 'development', philosophy: 'teacher-led', philosophyFit: 35, ratings: { coaching: 74, tactics: 67, development: 78, communication: 72, conditioning: 55 }, salary: 0.58, contractYears: 3, reputation: 72, loyalty: 69, ambition: 78, poachingRisk: 0 },
  'ivo-sayer': { specialty: 'attack', philosophy: 'front-foot', philosophyFit: 70, ratings: { coaching: 70, tactics: 70, development: 66, communication: 72, conditioning: 54 }, salary: 0.49, contractYears: 2, reputation: 64, loyalty: 61, ambition: 70, poachingRisk: 0 },
  'tessa-byrne': { specialty: 'conditioning', philosophy: 'availability-first', philosophyFit: 86, ratings: { coaching: 69, tactics: 58, development: 66, communication: 66, conditioning: 82 }, salary: 0.56, contractYears: 3, reputation: 70, loyalty: 77, ambition: 57, poachingRisk: 0 },
}

const fallbackDetails = (member: StaffMember): Omit<AssistantCoach, 'id' | 'name'> => ({
  specialty: specialtyFor(member), philosophy: 'adaptive', philosophyFit: 65,
  ratings: { coaching: 65, tactics: 65, development: 65, communication: 65, conditioning: 65 },
  salary: 0.45, contractYears: 2, reputation: 60, loyalty: 65, ambition: 60, poachingRisk: 2,
})

const assistantFromMember = (member: StaffMember): AssistantCoach => ({
  id: member.id,
  name: member.name,
  ...(initialDetails[member.id] ?? fallbackDetails(member)),
})

export const createAnnualStaffMarket = (seed: number, season: number): StaffMarketCandidate[] =>
  SPECIALTIES.flatMap((specialty, specialtyIndex) => Array.from({ length: 3 }, (_, index): StaffMarketCandidate => {
    const key = `${season}:${specialty}:${index}`
    const quality = seeded(seed, `${key}:quality`, 54, 84)
    const philosophy = PHILOSOPHIES[seeded(seed, `${key}:philosophy`, 0, PHILOSOPHIES.length - 1)]
    const nameIndex = specialtyIndex * 3 + index
    const salary = money(0.3 + quality * 0.006 + seeded(seed, `${key}:salary`, 0, 12) / 100)
    return {
      id: `s${season}-${specialty}-${index + 1}`,
      name: `${FIRST_NAMES[(nameIndex + seeded(seed, `${key}:first`, 0, 11)) % FIRST_NAMES.length]} ${LAST_NAMES[(nameIndex * 5 + seeded(seed, `${key}:last`, 0, 11)) % LAST_NAMES.length]}`,
      specialty,
      philosophy,
      philosophyFit: seeded(seed, `${key}:fit`, 42, 91),
      ratings: {
        coaching: clamp(quality + seeded(seed, `${key}:coaching`, -6, 7), 40, 92),
        tactics: clamp(quality + (specialty === 'attack' || specialty === 'defence' ? 7 : -3) + seeded(seed, `${key}:tactics`, -5, 5), 40, 94),
        development: clamp(quality + (specialty === 'development' ? 10 : 0) + seeded(seed, `${key}:development`, -5, 5), 40, 95),
        communication: clamp(quality + seeded(seed, `${key}:communication`, -8, 8), 40, 92),
        conditioning: clamp(quality + (specialty === 'conditioning' ? 11 : -5) + seeded(seed, `${key}:conditioning`, -4, 5), 40, 96),
      },
      salary,
      contractYears: seeded(seed, `${key}:years`, 1, 4),
      reputation: clamp(quality + seeded(seed, `${key}:reputation`, -8, 8), 35, 95),
      loyalty: seeded(seed, `${key}:loyalty`, 42, 88),
      ambition: seeded(seed, `${key}:ambition`, 48, 92),
      poachingRisk: seeded(seed, `${key}:poaching`, 3, 18),
      availableSeason: season,
      signingFee: money(salary * (0.2 + seeded(seed, `${key}:fee`, 0, 15) / 100)),
    }
  }))

export const calculateStaffEffects = (roster: AssistantCoach[]): StaffMarketEffects => {
  const developmentEfficiencyByCoach = Object.fromEntries(roster.map(coach => {
    const score = coach.ratings.development * 0.6 + coach.ratings.communication * 0.25 + coach.philosophyFit * 0.15
    return [coach.id, clamp(roundMultiplier(1 + (score - 70) / 100), 0.8, 1.25)]
  }))
  const tacticalCoaches = roster.filter(coach => coach.specialty === 'attack' || coach.specialty === 'defence')
  const tacticalScore = tacticalCoaches.length
    ? tacticalCoaches.reduce((sum, coach) => sum + coach.ratings.tactics * 0.7 + coach.philosophyFit * 0.3, 0) / tacticalCoaches.length
    : 55
  const conditioning = roster.find(coach => coach.specialty === 'conditioning')
  return {
    developmentEfficiencyByCoach,
    tacticalCohesionMultiplier: clamp(roundMultiplier(1 + (tacticalScore - 72) / 120), 0.8, 1.2),
    conditioningMultiplier: conditioning
      ? clamp(roundMultiplier(1 + (conditioning.ratings.conditioning * 0.75 + conditioning.philosophyFit * 0.25 - 80) / 120), 0.8, 1.2)
      : 0.8,
  }
}

const grade = (rating: number): AbilityGrade => rating >= 90 ? 'Elite' : rating >= 80 ? 'Strong' : rating >= 70 ? 'Above Standard' : rating >= 58 ? 'League Standard' : rating >= 47 ? 'Below Standard' : 'Limited'
const roleFor = (specialty: StaffSpecialty) => specialty === 'attack' ? 'Forwards Coach' : specialty === 'defence' ? 'Backline Coach' : specialty === 'development' ? 'Development Coach' : 'Fitness Coach'

const memberFromAssistant = (coach: AssistantCoach): StaffMember => ({
  id: coach.id,
  name: coach.name,
  role: roleFor(coach.specialty),
  isSeniorCoach: false,
  abilities: {
    Coaching: grade(coach.ratings.coaching), Tactics: grade(coach.ratings.tactics), Teaching: grade(coach.ratings.development),
    Communication: grade(coach.ratings.communication), Conditioning: grade(coach.ratings.conditioning),
  },
  developmentFocuses: ['Coaching', 'Tactics', 'Teaching', 'Communication', 'Conditioning'],
  teachingStyle: coach.philosophy === 'teacher-led' ? 'Evidence-led and encouraging' : 'Role-specific and accountable',
  tacticalAlignment: `${coach.philosophyFit}/100 philosophy fit`,
  relationships: grade(coach.ratings.communication),
  ambition: grade(coach.ambition),
  loyalty: grade(coach.loyalty),
  workload: 65,
  reputation: grade(coach.reputation),
  poachability: coach.poachingRisk,
  characteristics: [coach.philosophy],
  expertise: [coach.specialty],
  philosophy: coach.philosophy,
  recommendation: `${roleFor(coach.specialty)} programme`,
})

const syncRoster = (state: GameState, staffMarket: StaffMarketState): GameState => ({
  ...state,
  staffMarket,
  club: { ...state.club, staff: staffMarket.roster.map(memberFromAssistant) },
})

export const createStaffMarketState = (staff: StaffMember[], seed: number, season: number): StaffMarketState => {
  const roster = staff.filter(member => !member.isSeniorCoach).map(assistantFromMember)
  return {
    version: 52,
    season,
    seed,
    roster,
    candidates: createAnnualStaffMarket(seed, season),
    transactions: [],
    poachingOutcomes: [],
    processedRounds: [],
    developedSeasons: [],
    rolledSeasons: [season],
    effects: calculateStaffEffects(roster),
  }
}

const isSpecialty = (value: unknown): value is StaffSpecialty => SPECIALTIES.includes(value as StaffSpecialty)
const isPhilosophy = (value: unknown): value is StaffPhilosophy => PHILOSOPHIES.includes(value as StaffPhilosophy)
const sanitizeRatings = (raw: Partial<StaffRatings> | undefined): StaffRatings => ({
  coaching: validNumber(raw?.coaching, 60), tactics: validNumber(raw?.tactics, 60), development: validNumber(raw?.development, 60),
  communication: validNumber(raw?.communication, 60), conditioning: validNumber(raw?.conditioning, 60),
})
const sanitizeCoach = (raw: Partial<AssistantCoach>, fallback?: AssistantCoach): AssistantCoach | null => {
  if (typeof raw.id !== 'string' || !raw.id || typeof raw.name !== 'string' || !raw.name || !isSpecialty(raw.specialty)) return null
  return {
    id: raw.id,
    name: raw.name,
    specialty: raw.specialty,
    philosophy: isPhilosophy(raw.philosophy) ? raw.philosophy : fallback?.philosophy ?? 'adaptive',
    philosophyFit: validNumber(raw.philosophyFit, fallback?.philosophyFit ?? 60),
    ratings: sanitizeRatings(raw.ratings),
    salary: money(validNumber(raw.salary, fallback?.salary ?? 0.45, 0.1, 3)),
    contractYears: Math.floor(validNumber(raw.contractYears, fallback?.contractYears ?? 2, 1, 5)),
    reputation: validNumber(raw.reputation, fallback?.reputation ?? 60),
    loyalty: validNumber(raw.loyalty, fallback?.loyalty ?? 60),
    ambition: validNumber(raw.ambition, fallback?.ambition ?? 60),
    poachingRisk: validNumber(raw.poachingRisk, fallback?.poachingRisk ?? 2),
  }
}

export const hydrateStaffMarket = (state: GameState, saved?: Partial<StaffMarketState>): GameState => {
  const season = state.season?.season ?? saved?.season ?? 1
  const seed = state.season?.seed ?? saved?.seed ?? state.seed
  const canonical = createStaffMarketState(state.club.staff, seed, season)
  if (!saved) return syncRoster(state, canonical)
  const rosterBySpecialty = new Map<StaffSpecialty, AssistantCoach>()
  for (const raw of Array.isArray(saved.roster) ? saved.roster : canonical.roster) {
    const coach = sanitizeCoach(raw, canonical.roster.find(item => item.id === raw.id))
    if (coach && !rosterBySpecialty.has(coach.specialty)) rosterBySpecialty.set(coach.specialty, coach)
  }
  const roster = [...rosterBySpecialty.values()]
  const candidates = saved.season === season && Array.isArray(saved.candidates) && saved.candidates.length === 12
    ? saved.candidates.flatMap(raw => {
        const coach = sanitizeCoach(raw)
        return coach && Number(raw.availableSeason) === season
          ? [{ ...coach, availableSeason: season, signingFee: money(validNumber(raw.signingFee, coach.salary * 0.25, 0, 3)) }]
          : []
      })
    : createAnnualStaffMarket(seed, season)
  const staffMarket: StaffMarketState = {
    version: 52,
    season,
    seed,
    roster,
    candidates: candidates.length === 12 ? candidates : createAnnualStaffMarket(seed, season),
    transactions: Array.isArray(saved.transactions) ? saved.transactions : [],
    poachingOutcomes: Array.isArray(saved.poachingOutcomes) ? saved.poachingOutcomes : [],
    processedRounds: Array.isArray(saved.processedRounds) ? [...new Set(saved.processedRounds.filter(value => typeof value === 'string'))] : [],
    developedSeasons: Array.isArray(saved.developedSeasons) ? [...new Set(saved.developedSeasons.filter(Number.isInteger))] : [],
    rolledSeasons: Array.isArray(saved.rolledSeasons) ? [...new Set(saved.rolledSeasons.filter(Number.isInteger))] : [season],
    effects: calculateStaffEffects(roster),
  }
  return syncRoster(state, staffMarket)
}

export interface StaffMoveQuote {
  salary: number
  signingFee: number
  releaseCost: number
  totalCost: number
}

export const staffFiringCost = (coach: AssistantCoach) => money(coach.salary * coach.contractYears * 0.5)
export const quoteStaffMove = (state: GameState, candidateId: string, replacedCoachId?: string): StaffMoveQuote | null => {
  const candidate = state.staffMarket.candidates.find(item => item.id === candidateId)
  if (!candidate) return null
  const replaced = replacedCoachId ? state.staffMarket.roster.find(item => item.id === replacedCoachId) : undefined
  if (replacedCoachId && (!replaced || replaced.specialty !== candidate.specialty)) return null
  const releaseCost = replaced ? staffFiringCost(replaced) : 0
  return { salary: candidate.salary, signingFee: candidate.signingFee, releaseCost, totalCost: money(candidate.salary + candidate.signingFee + releaseCost) }
}

const transactionId = (state: GameState, type: string, coachId: string) => `s${state.staffMarket.season}-r${state.round}-${type}-${coachId}`

export const hireAssistant = (state: GameState, candidateId: string): GameState => {
  const candidate = state.staffMarket.candidates.find(item => item.id === candidateId)
  if (!candidate || state.staffMarket.roster.some(coach => coach.specialty === candidate.specialty)) return state
  const quote = quoteStaffMove(state, candidateId)
  if (!quote || state.club.resources.cash < quote.totalCost) return state
  const roster = [...state.staffMarket.roster, candidate]
  const staffMarket: StaffMarketState = {
    ...state.staffMarket,
    roster,
    candidates: state.staffMarket.candidates.filter(item => item.id !== candidateId),
    effects: calculateStaffEffects(roster),
    transactions: [...state.staffMarket.transactions, {
      id: transactionId(state, 'hired', candidate.id), season: state.staffMarket.season, round: state.round,
      type: 'hired', coachId: candidate.id, coachName: candidate.name, specialty: candidate.specialty,
      cost: quote.totalCost, compensation: 0,
    }],
  }
  return syncRoster({ ...state, club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash - quote.totalCost) } } }, staffMarket)
}

export const replaceAssistant = (state: GameState, incumbentId: string, candidateId: string): GameState => {
  const incumbent = state.staffMarket.roster.find(item => item.id === incumbentId)
  const candidate = state.staffMarket.candidates.find(item => item.id === candidateId)
  const quote = quoteStaffMove(state, candidateId, incumbentId)
  if (!incumbent || !candidate || !quote || state.club.resources.cash < quote.totalCost) return state
  const roster = state.staffMarket.roster.map(coach => coach.id === incumbentId ? candidate : coach)
  const staffMarket: StaffMarketState = {
    ...state.staffMarket,
    roster,
    candidates: state.staffMarket.candidates.filter(item => item.id !== candidateId),
    effects: calculateStaffEffects(roster),
    transactions: [...state.staffMarket.transactions, {
      id: transactionId(state, 'replaced', candidate.id), season: state.staffMarket.season, round: state.round,
      type: 'replaced', coachId: candidate.id, coachName: candidate.name, specialty: candidate.specialty,
      cost: quote.totalCost, compensation: 0, replacedCoachId: incumbent.id,
    }],
  }
  return syncRoster({ ...state, club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash - quote.totalCost) } } }, staffMarket)
}

export const fireAssistant = (state: GameState, coachId: string): GameState => {
  const coach = state.staffMarket.roster.find(item => item.id === coachId)
  if (!coach) return state
  const cost = staffFiringCost(coach)
  if (state.club.resources.cash < cost) return state
  const roster = state.staffMarket.roster.filter(item => item.id !== coachId)
  const staffMarket: StaffMarketState = {
    ...state.staffMarket,
    roster,
    effects: calculateStaffEffects(roster),
    transactions: [...state.staffMarket.transactions, {
      id: transactionId(state, 'fired', coach.id), season: state.staffMarket.season, round: state.round,
      type: 'fired', coachId: coach.id, coachName: coach.name, specialty: coach.specialty,
      cost, compensation: 0,
    }],
  }
  return syncRoster({ ...state, club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash - cost) } } }, staffMarket)
}

export const processStaffPoaching = (state: GameState, completedRound: number): GameState => {
  const season = state.season?.season ?? state.staffMarket.season
  const roundKey = `s${season}-r${completedRound}`
  if (state.staffMarket.processedRounds.includes(roundKey)) return state
  let cash = state.club.resources.cash
  const roster: AssistantCoach[] = []
  const transactions = [...state.staffMarket.transactions]
  const outcomes = [...state.staffMarket.poachingOutcomes]
  for (const coach of state.staffMarket.roster) {
    const risk = clamp(Math.round(coach.poachingRisk + Math.max(0, coach.reputation - 75) * 0.2 + Math.max(0, coach.ambition - coach.loyalty) * 0.15), 0, 100)
    const draw = (hash(`${state.staffMarket.seed}:s${season}:r${completedRound}:${coach.id}:poach`) % 10_000) + 1
    const poached = draw <= risk * 100
    outcomes.push({ id: `${roundKey}-${coach.id}`, season, round: completedRound, coachId: coach.id, risk, draw, result: poached ? 'poached' : 'retained' })
    if (!poached) { roster.push(coach); continue }
    const compensation = money(coach.salary * 0.5)
    cash = money(cash + compensation)
    transactions.push({
      id: `${roundKey}-poached-${coach.id}`, season, round: completedRound, type: 'poached',
      coachId: coach.id, coachName: coach.name, specialty: coach.specialty, cost: 0, compensation,
    })
  }
  return syncRoster({ ...state, club: { ...state.club, resources: { ...state.club.resources, cash } } }, {
    ...state.staffMarket,
    roster,
    transactions,
    poachingOutcomes: outcomes,
    processedRounds: [...state.staffMarket.processedRounds, roundKey],
    effects: calculateStaffEffects(roster),
  })
}

const ratingForFocus = (focus: string): keyof StaffRatings => {
  const value = focus.toLowerCase()
  if (value.includes('tactic') || value.includes('structure') || value.includes('craft')) return 'tactics'
  if (value.includes('teach') || value.includes('talent') || value.includes('player')) return 'development'
  if (value.includes('injury') || value.includes('load') || value.includes('rehab') || value.includes('condition')) return 'conditioning'
  if (value.includes('communication') || value.includes('relationship')) return 'communication'
  return 'coaching'
}

const developCoach = (coach: AssistantCoach, allocation: CoachDevelopmentAllocation): AssistantCoach => {
  const target = ratingForFocus(allocation.focus)
  const gain = Math.max(1, Math.round(Math.sqrt(Math.max(0, allocation.points)) * 3))
  return {
    ...coach,
    ratings: { ...coach.ratings, [target]: clamp(coach.ratings[target] + gain, 0, 100) },
    reputation: clamp(coach.reputation + Math.ceil(allocation.points / 2), 0, 100),
    poachingRisk: clamp(coach.poachingRisk + allocation.points * 2, 0, 100),
  }
}

export const applyAnnualStaffDevelopment = (state: GameState, season = state.coachDevelopment.season): GameState => {
  if (state.staffMarket.developedSeasons.includes(season)) return state
  const roster = state.staffMarket.roster.map(coach => {
    const allocations = state.coachDevelopment.allocations.filter(item => item.coachId === coach.id && item.points > 0)
    return allocations.reduce(developCoach, coach)
  })
  return syncRoster(state, {
    ...state.staffMarket,
    roster,
    developedSeasons: [...state.staffMarket.developedSeasons, season],
    effects: calculateStaffEffects(roster),
  })
}

export const rollStaffMarketSeason = (state: GameState, nextSeason: number): GameState => {
  if (state.staffMarket.rolledSeasons.includes(nextSeason)) return state
  const developed = applyAnnualStaffDevelopment(state, state.staffMarket.season)
  const roster: AssistantCoach[] = []
  const transactions = [...developed.staffMarket.transactions]
  for (const coach of developed.staffMarket.roster) {
    const contractYears = coach.contractYears - 1
    if (contractYears > 0) roster.push({ ...coach, contractYears })
    else transactions.push({
      id: `s${nextSeason}-contract-expired-${coach.id}`, season: nextSeason, round: 0, type: 'contract-expired',
      coachId: coach.id, coachName: coach.name, specialty: coach.specialty, cost: 0, compensation: 0,
    })
  }
  const seed = (state.season?.seed ?? developed.staffMarket.seed) + (nextSeason * 977)
  return syncRoster(developed, {
    ...developed.staffMarket,
    season: nextSeason,
    seed,
    roster,
    candidates: createAnnualStaffMarket(seed, nextSeason),
    transactions,
    processedRounds: [],
    rolledSeasons: [...developed.staffMarket.rolledSeasons, nextSeason],
    effects: calculateStaffEffects(roster),
  })
}

export const staffDevelopmentEfficiency = (state: GameState, coachId?: string) =>
  coachId ? state.staffMarket?.effects.developmentEfficiencyByCoach[coachId] ?? 0.85 : 1

export const staffTacticalCohesionMultiplier = (state: GameState) => state.staffMarket?.effects.tacticalCohesionMultiplier ?? 1
